import React, { forwardRef } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';
import { TableSkeletonLoader } from '../SkeletonLoader';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';

type TableBodyProps = ViewProps & {
  children?: React.ReactNode;
  isLoading?: boolean;
  loadingRows?: number;
  loadingContent?: React.ReactNode;
  isEmpty?: boolean;
  emptyContent?: React.ReactNode;
};

/**
 * TableBody - Table body container
 *
 * Wraps TableRow components that contain TableCell cells.
 * Supports loading and empty states out of the box.
 *
 * @example Basic usage
 * ```tsx
 * <TableBody>
 *   {data.map(item => (
 *     <TableRow key={item.id}>
 *       <TableCell>{item.name}</TableCell>
 *       <TableCell>{item.email}</TableCell>
 *     </TableRow>
 *   ))}
 * </TableBody>
 * ```
 *
 * @example With simple loading state
 * ```tsx
 * <TableBody isLoading={isLoading} loadingRows={5}>
 *   {data.map(item => (
 *     <TableRow key={item.id}>
 *       <TableCell>{item.name}</TableCell>
 *     </TableRow>
 *   ))}
 * </TableBody>
 * ```
 *
 * @example With custom loading content (recommended for polished UX)
 * ```tsx
 * <TableBody
 *   isLoading={isLoading}
 *   loadingRows={5}
 *   loadingContent={
 *     <TableRow>
 *       <TableCell flex={2}><TableSkeletonLoader style={styles.skeletonBox} /></TableCell>
 *       <TableCell flex={1}><TableSkeletonLoader style={styles.skeletonCircle} /></TableCell>
 *     </TableRow>
 *   }
 * >
 *   {data.map(item => (
 *     <TableRow key={item.id}>
 *       <TableCell flex={2}>{item.name}</TableCell>
 *       <TableCell flex={1}>{item.avatar}</TableCell>
 *     </TableRow>
 *   ))}
 * </TableBody>
 * ```
 *
 * @example With empty state
 * ```tsx
 * <TableBody isEmpty={data.length === 0} emptyContent={<TextReg>No data found</TextReg>}>
 *   {data.map(item => (
 *     <TableRow key={item.id}>
 *       <TableCell>{item.name}</TableCell>
 *     </TableRow>
 *   ))}
 * </TableBody>
 * ```
 */
export const TableBody = forwardRef<View, TableBodyProps>(
  (
    {
      children,
      style,
      isLoading,
      loadingRows = 5,
      loadingContent,
      isEmpty,
      emptyContent,
      ...props
    },
    ref,
  ) => {
    const styles = useDynamicStyles(createStyles);

    // Show loading state
    if (isLoading) {
      return (
        <View ref={ref} style={[styles.body, style]} {...props}>
          {loadingContent
            ? // Custom loading content (user provides their own skeleton rows)
              Array.from({ length: loadingRows }).map((_, index) => (
                <React.Fragment key={`skeleton-${index}`}>
                  {loadingContent}
                </React.Fragment>
              ))
            : // Default simple loading (generic skeleton) - spans full row width
              Array.from({ length: loadingRows }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell style={styles.skeletonCell}>
                    <TableSkeletonLoader style={styles.skeleton} />
                  </TableCell>
                </TableRow>
              ))}
        </View>
      );
    }

    // Show empty state
    if (isEmpty) {
      return (
        <View
          ref={ref}
          style={[styles.body, styles.emptyBody, style]}
          {...props}
        >
          {emptyContent ? (
            emptyContent
          ) : (
            <TableRow style={styles.emptyBody}>
              <TableCell style={styles.emptyCell}>
                <View />
              </TableCell>
            </TableRow>
          )}
        </View>
      );
    }

    return (
      <View ref={ref} style={[styles.body, style]} {...props}>
        {children}
      </View>
    );
  },
);

TableBody.displayName = 'TableBody';

const createStyles = () =>
  StyleSheet.create({
    body: {
      width: '100%',
    },
    skeletonCell: {
      flex: 1,
      width: '100%',
    },
    skeleton: {
      height: 24,
      backgroundColor: Colors.opacity20,
      borderRadius: 4,
      width: '85%',
    },
    emptyBody: {
      paddingHorizontal: 16,
    },
    emptyCell: {
      flex: 1,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 32,
    },
  });
